import { CSSProperties } from 'react';

import {
    Close,
    AccessTime,
    CallSplit,
    DeviceThermostat,
    DoNotDisturb,
    MailOutline,
    SupervisedUserCircleOutlined,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { Handle, Position } from 'reactflow';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import useAudienceData from 'domains/campaigns/components/CustomCampaign/CustomCampaignNavigation/CustomCampaignNavigationBlock/useAudienceData';
import CustomCampaignNavigationButton from 'domains/campaigns/components/CustomCampaign/CustomCampaignNavigation/CustomCampaignNavigationButton';
import { channelIcons } from 'domains/campaigns/pages/CurationsPage/utils';
import {
    CampaignChannelLabels,
    CampaignChannels,
    GraphNodeData,
    NodeType,
    SplitActions,
} from 'domains/campaigns/types';
import { Template } from 'domains/content/types';
import IconButton from 'domains/core/components/IconButton';
import useTemplates from 'hooks/queries/useTemplates';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const hiddenHandleStyles: CSSProperties = { visibility: 'hidden' };

// White background ensures the node occludes any edges that route behind it,
// matching how LegacyNavigationNode uses backgroundColor: '#fff'.
const nodeWrapperStyles: CSSProperties = { backgroundColor: '#fff' };

const formatDelayInterval = (interval?: string): string | undefined => {
    if (!interval) return undefined;
    const parts = interval.split(' ');
    if (parts.length < 2) return interval;
    const value = Number(parts[0]);
    const unitSingular = parts[1].replace(/s$/, '');
    return `${value} ${value > 1 ? `${unitSingular}s` : unitSingular}`;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CustomFlowNodeData = GraphNodeData & {
    nodeType: NodeType;
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    readonly: boolean;
    setActiveSendIndex?: (index: number) => void;
    setActiveStep?: (step: CustomCampaignSteps) => void;
    canRemove?: boolean;
    onRemoveClick?: (index: number) => void;
};

export type Props = {
    isConnectable: boolean;
    data: CustomFlowNodeData;
    sourcePosition?: Position;
    targetPosition?: Position;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CustomFlowNode = ({ data, isConnectable, sourcePosition, targetPosition }: Props) => {
    const {
        nodeType,
        activeSendIndex,
        activeStep,
        readonly,
        setActiveSendIndex,
        setActiveStep,
        canRemove,
        onRemoveClick,
    } = data;

    const { currentAudience } = useAudienceData({
        segmentId: nodeType === 'audience' ? data?.segmentId : undefined,
        cohortId: nodeType === 'audience' ? data?.cohortId : undefined,
    });

    const isEmailSend = nodeType === 'send' && data?.channel === CampaignChannels.EMAIL;
    const { data: templates } = useTemplates({ enabled: isEmailSend });
    const templateName =
        isEmailSend && data?.templateId ? templates?.find((t: Template) => t.id === data?.templateId)?.name : undefined;

    const navigate = (step: CustomCampaignSteps, sendIndex?: number) => {
        if (readonly) return;
        if (setActiveStep) setActiveStep(step);
        if (setActiveSendIndex && sendIndex != null) setActiveSendIndex(sendIndex);
    };

    // -----------------------------------------------------------------------
    // audience — only render once an audience has been chosen
    // -----------------------------------------------------------------------
    if (nodeType === 'audience') {
        if (!data?.segmentId && !data?.cohortId) return null;
        return (
            <div data-testid="graph-node-audience" aria-label="audience" style={nodeWrapperStyles}>
                <Handle
                    isConnectable={isConnectable}
                    type="target"
                    position={targetPosition ?? Position.Top}
                    style={hiddenHandleStyles}
                />
                <CustomCampaignNavigationButton
                    data-testid="nav-button-audience"
                    icon={<SupervisedUserCircleOutlined />}
                    isActive={!readonly && activeStep === CustomCampaignSteps.SELECT_AUDIENCE}
                    label="audience"
                    onClick={readonly ? undefined : () => navigate(CustomCampaignSteps.SELECT_AUDIENCE, 0)}
                    value={currentAudience?.name}
                />
                <Handle
                    isConnectable={isConnectable}
                    type="source"
                    position={sourcePosition ?? Position.Bottom}
                    style={hiddenHandleStyles}
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // ab_test
    // -----------------------------------------------------------------------
    if (nodeType === 'ab_test') {
        return (
            <div data-testid="graph-node-ab-test" aria-label="A/B test" style={nodeWrapperStyles}>
                <Handle
                    isConnectable={isConnectable}
                    type="target"
                    position={targetPosition ?? Position.Top}
                    style={hiddenHandleStyles}
                />
                <CustomCampaignNavigationButton
                    data-testid="nav-button-ab_test"
                    icon={<DeviceThermostat />}
                    isActive={
                        !readonly &&
                        activeStep === CustomCampaignSteps.SETUP_A_B_TEST &&
                        data?.sendIndex === activeSendIndex
                    }
                    label="A/B test"
                    onClick={readonly ? undefined : () => navigate(CustomCampaignSteps.SETUP_A_B_TEST, data?.sendIndex)}
                    value={data?.abTestConfig?.testOn ?? ''}
                />
                <Handle
                    isConnectable={isConnectable}
                    type="source"
                    position={sourcePosition ?? Position.Bottom}
                    style={hiddenHandleStyles}
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // time_delay
    // -----------------------------------------------------------------------
    if (nodeType === 'time_delay') {
        return (
            <div data-testid="graph-node-time-delay" aria-label="time delay" style={nodeWrapperStyles}>
                <Handle
                    isConnectable={isConnectable}
                    type="target"
                    position={targetPosition ?? Position.Top}
                    style={hiddenHandleStyles}
                />
                <CustomCampaignNavigationButton
                    data-testid="nav-button-time_delay"
                    icon={<AccessTime />}
                    isActive={
                        !readonly &&
                        activeStep === CustomCampaignSteps.SET_TIME_DELAY &&
                        data?.sendIndex === activeSendIndex
                    }
                    label="time delay"
                    onClick={readonly ? undefined : () => navigate(CustomCampaignSteps.SET_TIME_DELAY, data?.sendIndex)}
                    value={formatDelayInterval(data?.interval)}
                />
                <Handle
                    isConnectable={isConnectable}
                    type="source"
                    position={sourcePosition ?? Position.Bottom}
                    style={hiddenHandleStyles}
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // conditional_split
    // -----------------------------------------------------------------------
    if (nodeType === 'conditional_split') {
        return (
            <div data-testid="graph-node-conditional-split" aria-label="conditional split" style={nodeWrapperStyles}>
                <Handle
                    isConnectable={isConnectable}
                    type="target"
                    position={targetPosition ?? Position.Top}
                    style={hiddenHandleStyles}
                />
                <CustomCampaignNavigationButton
                    data-testid="nav-button-conditional_split"
                    icon={<CallSplit />}
                    isActive={
                        !readonly &&
                        activeStep === CustomCampaignSteps.CONDITIONAL_SPLIT &&
                        data?.sendIndex === activeSendIndex
                    }
                    label="conditional split"
                    onClick={
                        readonly ? undefined : () => navigate(CustomCampaignSteps.CONDITIONAL_SPLIT, data?.sendIndex)
                    }
                />
                <Handle
                    isConnectable={isConnectable}
                    type="source"
                    position={sourcePosition ?? Position.Bottom}
                    style={hiddenHandleStyles}
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // send
    // -----------------------------------------------------------------------
    if (nodeType === 'send') {
        const channel = data?.channel;
        const splitType = data?.splitType;
        const splitAction = data?.splitAction;
        const isBranchSend = !!splitType;

        // Split branch send with no action chosen yet — show disabled choose-action button
        if (isBranchSend && !splitAction) {
            return (
                <div data-testid="graph-node-send" aria-label={splitType ?? 'choose action'} style={nodeWrapperStyles}>
                    <Handle
                        isConnectable={isConnectable}
                        type="target"
                        position={targetPosition ?? Position.Top}
                        style={hiddenHandleStyles}
                    />
                    <CustomCampaignNavigationButton
                        data-testid="nav-button-send"
                        disabled
                        icon={<CallSplit />}
                        isActive={false}
                        label={splitType ?? 'choose action'}
                        onClick={
                            readonly
                                ? undefined
                                : () => navigate(CustomCampaignSteps.SELECT_PATH_ACTION, data?.sendIndex)
                        }
                    />
                    <Handle
                        isConnectable={isConnectable}
                        type="source"
                        position={sourcePosition ?? Position.Bottom}
                        style={hiddenHandleStyles}
                    />
                </div>
            );
        }

        // Exit branch (splitAction === EXIT)
        if (splitAction === SplitActions.EXIT) {
            return (
                <div data-testid="graph-node-send" aria-label="exit curation" style={nodeWrapperStyles}>
                    <Handle
                        isConnectable={isConnectable}
                        type="target"
                        position={targetPosition ?? Position.Top}
                        style={hiddenHandleStyles}
                    />
                    <CustomCampaignNavigationButton
                        data-testid="nav-button-send"
                        icon={<DoNotDisturb />}
                        isActive={
                            !readonly &&
                            activeStep === CustomCampaignSteps.SELECT_PATH_ACTION &&
                            data?.sendIndex === activeSendIndex
                        }
                        label="exit curation"
                        onClick={
                            readonly
                                ? undefined
                                : () => navigate(CustomCampaignSteps.SELECT_PATH_ACTION, data?.sendIndex)
                        }
                    />
                    <Handle
                        isConnectable={isConnectable}
                        type="source"
                        position={sourcePosition ?? Position.Bottom}
                        style={hiddenHandleStyles}
                    />
                </div>
            );
        }

        // Email send with no template selected and not a branch send — hide until configured
        if (channel === CampaignChannels.EMAIL && !data?.templateId && !isBranchSend) {
            return null;
        }

        const SendIcon = channel && channelIcons[channel] ? channelIcons[channel] : MailOutline;
        const channelLabel = channel ? CampaignChannelLabels[channel] ?? channel : 'send';

        return (
            <Box
                data-testid="graph-node-send"
                aria-label={channelLabel}
                sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff' }}
            >
                <Handle
                    isConnectable={isConnectable}
                    type="target"
                    position={targetPosition ?? Position.Top}
                    style={hiddenHandleStyles}
                />
                <Box sx={{ flex: 1 }}>
                    <CustomCampaignNavigationButton
                        data-testid="nav-button-send"
                        icon={<SendIcon />}
                        isActive={
                            !readonly &&
                            (activeStep === CustomCampaignSteps.SELECT_CONTENT ||
                                activeStep === CustomCampaignSteps.SCHEDULE_CURATION) &&
                            data?.sendIndex === activeSendIndex
                        }
                        label={channelLabel}
                        onClick={
                            readonly
                                ? undefined
                                : () =>
                                      navigate(
                                          isBranchSend
                                              ? CustomCampaignSteps.SELECT_PATH_ACTION
                                              : CustomCampaignSteps.SELECT_CONTENT,
                                          data?.sendIndex
                                      )
                        }
                        value={channel === CampaignChannels.EMAIL ? templateName : data?.sendName}
                    />
                </Box>
                {canRemove && data?.sendIndex != null && (
                    <IconButton
                        data-testid="graph-node-send-remove"
                        handleClick={() => onRemoveClick?.(data.sendIndex)}
                        Icon={Close}
                        title="remove send"
                    />
                )}
                <Handle
                    isConnectable={isConnectable}
                    type="source"
                    position={sourcePosition ?? Position.Bottom}
                    style={hiddenHandleStyles}
                />
            </Box>
        );
    }

    // -----------------------------------------------------------------------
    // exit
    // -----------------------------------------------------------------------
    if (nodeType === 'exit') {
        return (
            <div data-testid="graph-node-exit" aria-label="exit curation" style={nodeWrapperStyles}>
                <Handle
                    isConnectable={isConnectable}
                    type="target"
                    position={targetPosition ?? Position.Top}
                    style={hiddenHandleStyles}
                />
                <CustomCampaignNavigationButton
                    data-testid="nav-button-exit"
                    icon={<DoNotDisturb />}
                    label="exit curation"
                    onClick={undefined}
                />
                <Handle
                    isConnectable={isConnectable}
                    type="source"
                    position={sourcePosition ?? Position.Bottom}
                    style={hiddenHandleStyles}
                />
            </div>
        );
    }

    return null;
};

export default CustomFlowNode;
